import { create } from 'zustand';

/**
 * Normalized player states for the YouTube IFrame API.
 *
 * - 'idle':      Player is created but no video has started yet (YT.PlayerState.UNSTARTED = -1, CUED = 5).
 * - 'playing':   Video is actively playing (YT.PlayerState.PLAYING = 1).
 * - 'paused':    Video is paused (YT.PlayerState.PAUSED = 2).
 * - 'buffering': Video is buffering (YT.PlayerState.BUFFERING = 3).
 * - 'ended':     Playback reached the end (YT.PlayerState.ENDED = 0).
 * - 'ad':        Reserved for ad-state coordination in Story 2.4; structure defined now
 *                so later stories can read playerState without re-implementing ad-hoc tracking.
 */
export type VideoPlayerState = 'idle' | 'playing' | 'paused' | 'buffering' | 'ended' | 'ad';

interface VideoPlayerStoreState {
  /** Current playback position in seconds. Updated via onTimeUpdate or polling. */
  currentTime: number;
  /** Normalized player state. Only YouTubePlayer.tsx writes this for the current story. */
  playerState: VideoPlayerState;
  /** ID of the active segment being looped/highlighted (Story 2.2+). */
  activeSegmentId: string | null;
  /** Whether loop mode is active (Story 2.3+). */
  isLooping: boolean;
  /**
   * ID of the last segment the user actively studied (fetched transcript or entered edit mode).
   * Story 2.6: Used for Resume mode — "last-worked" chip accent + Resume button.
   * Intentionally NOT cleared by reset() so it survives loop navigation within a session.
   */
  lastWorkedSegmentId: string | null;

  // Actions
  setCurrentTime: (time: number) => void;
  setPlayerState: (state: VideoPlayerState) => void;
  setActiveSegmentId: (id: string | null) => void;
  setIsLooping: (looping: boolean) => void;
  setLastWorkedSegmentId: (id: string | null) => void;
  reset: () => void;
}

const initialState = {
  currentTime: 0,
  playerState: 'idle' as VideoPlayerState,
  activeSegmentId: null,
  isLooping: false,
  // lastWorkedSegmentId is intentionally NOT in initialState so reset() does not clear it.
};

/**
 * videoPlayerStore is the shared source of truth for YouTube video playback state.
 *
 * Rules (from architecture):
 * - Store CLIENT/UI/player state only — no server-fetched video data lives here.
 * - YouTubePlayer.tsx is the primary writer for playerState and currentTime in this story.
 * - Later listening-time system should read playerState from here instead of re-implementing
 *   ad-hoc player tracking in multiple components.
 * - activeSegmentId and isLooping are stubbed now to allow Story 2.2/2.3 to build on this.
 * - lastWorkedSegmentId (Story 2.6): set when user fetches transcript or enters edit mode;
 *   NOT cleared by reset() — must survive loop navigation within a session.
 */
export const useVideoPlayerStore = create<VideoPlayerStoreState>((set) => ({
  ...initialState,
  lastWorkedSegmentId: null,

  setCurrentTime: (time) => set({ currentTime: time }),
  setPlayerState: (state) => set({ playerState: state }),
  setActiveSegmentId: (id) => set({ activeSegmentId: id }),
  setIsLooping: (looping) => set({ isLooping: looping }),
  setLastWorkedSegmentId: (id) => set({ lastWorkedSegmentId: id }),
  reset: () => set(initialState),
}));
